import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout1 from "../../DataLayouts/Layout1";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { fetchEmployeeLeaveEntitlements, fetchEmployeeLeaves } from "../../../Apis/Employee-api";
import { MAIN_URL } from "../../../Configurations/Urls";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import axios from "axios";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { Box, Typography } from "@mui/material";
import InboxIcon from "@mui/icons-material/Inbox";

const DEFAULT_COLUMNS = [
  {
    field: "carry_forward_days",
    label: "carry_forward_days",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "department_name",
    label: "department_name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "leave_type_name",
    label: "leave_type_name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "location_name",
    label: "location",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_leave_entitlement_id",
  sortOrder: "desc",
};

// Helper function to build filters from URL search parameters
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

function EmployeeLeaveEntitlementList() {
  const [leaves, setLeaves] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const {id} = useParams();

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

// Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(
          `${MAIN_URL}/api/general-datagrids`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `organization_leave_entitlements_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

      
          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // load data with filters
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");

        // Check if we need all data (for personalize view or when getAll=true in URL)
        const getAllData = searchParams.get("getAll") === "true" || 
                          window.location.pathname.includes("personalize");

        const baseBody = {
          filters,
          sort: {
            field: searchParams.get("sortBy") || "organization_leave_entitlement_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        // For personalize view, fetch all data without pagination
        const requestBody = getAllData ? {
          ...baseBody,
          page: 1,
          pageSize: 99999,
        } : {
          ...baseBody,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/employee-entitlements-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            // Only set pagination if not fetching all data
            if (!getAllData && result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            } else if (getAllData) {
              // When fetching all data, set pagination to show all records
              setPaginationData({
                page: 1,
                pageSize: data.length,
                total: data.length,
                totalPages: 1,
                hasNext: false,
                hasPrev: false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchEmployeeLeaveEntitlements(org.organization_id);
          data = oldData?.orgleaveEntitle?.data || oldData?.orgleaveEntitle || [];
          // If fetching all data, use all records, otherwise use pagination
          if (getAllData) {
            setPaginationData({
              page: 1,
              pageSize: data.length,
              total: data.length,
              totalPages: 1,
              hasNext: false,
              hasPrev: false,
            });
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Transform data - preserve existing transformation logic
        let b = data.map((item) => {
          return {
            ...item,
            id: item.organization_leave_entitlement_id,
            
            // Make sure leavetype is mapped correctly
            leave_type_name: item?.leavetype?.leave_type_name || item?.leave_type || "-",
            
            department_name: item?.department?.department_name || item?.department || "-",
            location_name: item?.location?.location_name || item?.location || "-",
            designation_name: item?.designation?.designation_name || item?.designation || "-",
            entitled_days: item?.entitled_days ?? "",
            entitlement_period: item?.entitlement_period || "-",
            work_shift_type_name: item?.workshifttype?.work_shift_type_name || item?.work_shift_type || "",
            work_shift_name: item?.workshift?.work_shift_name || item?.work_shift || "-",
            requires_approval: item?.requires_approval ? "✔" : "✖",
            is_active: item?.is_active ? "✔" : "✖",
            priority_level: item?.priority_level || "",
            encashment_allowed: item?.encashment_allowed ? "✔" : "✖"
          };
        });
        setLeaves(b);
      } catch (err) {
        if (err.name === "AbortError") return;
        
        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch leave entitlements:", err);
          toast.error("Failed to fetch leave entitlements");
          setLeaves([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);


  let deleteemployeeleave = async (id) => {
    try {
      const org_id = org.organization_id;
      const response = await axios.delete(
        `${MAIN_URL}/api/organizations/${org_id}/employee-entitlements/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

       if (response.status === 200) {
              toast.success(response.data.message);
              console.log(" Leave Entitlment Deleted:", response.data.message);
              // Refresh data after deletion by triggering useEffect
              setSearchParams((prev) => new URLSearchParams(prev));
            } else {
              const errorMessage =
                response.data.message ||
                response.data.errors?.[Object.keys(response.data.errors)[0]]?.[0] ||
                "Failed to delete Entitlment";
      
              toast.error(errorMessage);
              console.warn("Deletion error:", response.status, response.data);
            }


    } catch (error) { if (error.response && error.response.status === 401) {
  toast.error("Session Expired!");
  window.location.href = "/login";
}
      console.error("Delete failed:", error);
      toast.error(
        error.response?.data?.error || "Failed to delete Employee Leave Entitlement"
      );
    }
  };


  
    const handleDelete = async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/employee-entitlements/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        console.log("Successfully deleted leave entitlement with id:", id);
        // Refresh data after deletion by triggering useEffect
        setSearchParams((prev) => new URLSearchParams(prev));
        return Promise.resolve();
      } catch (error) {
        console.error("Delete failed:", error);
        return Promise.reject(error);
      }
    };


    const handleEdit = useCallback(

    (item) => {

         navigate(`/leave/employee-entitlements/edit/${item.id}`);

    },

    [navigate]

  );



   
      const handleShow = useCallback(
    (item) => {
      navigate(`/leave/employee-entitlements/view/${item.id}`)
    },
    [navigate],
  )

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(
    () => {
      setSearchParams((prev) => new URLSearchParams(prev));
    },
    [setSearchParams]
  );


  


  return (
    <>
    <Layout4
      loading={loading}
      heading={"Employee Leave Entitlement"}
      btnName={"Add Employee Leave Entitlement"}
      add_action={"EMPLOYEE_ENTITLEMENT_ADD"}
      delete_action={"EMPLOYEE_ENTITLEMENT_DELETE"}
      Data={leaves}
      tableHeaders={[
        { name: "Leave Type", value_key: "leavetype" },
        {
          name: "Entitle days",
          value_key: "entitledays",
          textStyle: "capitalize",
        },
        { name: "Entitle Period", value_key: "entitlement_period" },
         {
          name: "Department Name",
          value_key: "fullname",
          textStyle: "capitalize",
        },
        { name: "Location", value_key: "location" },
      ]}
      Icons={[
        <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
        <FormatAlignJustifyIcon color="primary" />,
        <CategoryIcon sx={{ color: "text.secondary" }} />,
        <DateRangeIcon sx={{ color: "text.secondary" }} />,
      ]}
      messages={[
        "Employee Leave Entitlement",
        "Employee Leave Entitlement",
        "Add Employee Leaves Entitlement",
        "Employee Leaves Entitlement",
      ]}
      Route={"/leave/employee-entitlements"}
      setData={setLeaves}
      DeleteFunc={deleteemployeeleave}
    />

    
 
    
      {leaves.length === 0 && !loading && !loadingConfig ? (
        <EmptyState />
      ) : (
        !loadingConfig && (
          <TableDataGeneric
            tableName="organization_leave_entitlements"
            primaryKey="organization_leave_entitlement_id"
            heading="Employee Leave Entitlement"
            data={leaves}
            sortname={"organization_leave_entitlement_id"}
            showActions={true}
            Route="/leave/employee-entitlements"
            DeleteFunc={handleDelete}
            handleShow={handleShow}
            EditFunc={handleEdit}
            edit_delete_action={["EMPLOYEE_ENTITLEMENT_DELETE", "EMPLOYEE_ENTITLEMENT_EDIT"]}
            token={localStorage.getItem("token")}
            configss={configColumns}
            paginationData={paginationData}
            onSortChange={handleSortChange}
            onFilterChange={handleFilterChange}
            filterModule="organization_leave_entitlements"
            {...(tableConfig && { config: tableConfig })}
          />
        )
      )}
    </>
  );
}

const EmptyState = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "300px",
      textAlign: "center",
      color: "text.secondary",
    }}
  >
    <InboxIcon sx={{ fontSize: 80, color: "grey.400", mb: 2 }} />
    <Typography variant="h6" gutterBottom>
      No Leave Entitlements Found
    </Typography>
    <Typography variant="body2">
      {window.location.search ? "Try adjusting your filters." : "Add new leave entitlements to get started."}
    </Typography>
  </Box>
);

export default EmployeeLeaveEntitlementList;
